import { DataLogStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import { DateOptions } from 'store/api/enums';
import i18n from 'i18next';

/**
 * Simple filter configuration for dashboard cards
 * Defines what filters to apply and how to display them in the UI
 */
export interface CardFilterConfig {
  // Filter definitions - what filters to apply
  filters: {
    [filterKey: string]: {
      value: string | string[] | [string, string]; // Filter value(s)
      displayInUI?: boolean; // Whether to show this filter in the UI (default: true)
    };
  };
  // Optional: exclude certain statuses regardless of status filter
  excludeStatuses?: DataLogStatus[];
}

/**
 * Get filter configuration for "My Open Returns" card
 */
export const getMyOpenReturnsFilterConfig = (currentUser?: User): CardFilterConfig | null => {
  if (!currentUser) return null;

  const userName =
    `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
    currentUser.email ||
    i18n.t('common.unknown');

  return {
    filters: {
      // User filter - searches across preparer, reviewer, and client (OR logic)
      user: {
        value: [userName],
        displayInUI: true,
      },
      // Status filter - open returns only (exclude CANCELLED, SUBMITTED, NOT_STARTED; include LOCKED)
      status: {
        value: [
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
        ],
        displayInUI: true,
      },
    },
    // Explicitly exclude these statuses as a safeguard
    excludeStatuses: [DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED, DataLogStatus.NOT_STARTED],
  };
};

/**
 * Get filter configuration for "Not Started" card
 */
export const getNotStartedFilterConfig = (): CardFilterConfig => {
  return {
    filters: {
      status: {
        value: [DataLogStatus.NOT_STARTED],
        displayInUI: true,
      },
    },
  };
};

/**
 * Get filter configuration for "Team Returns" (Pending Tasks) card
 */
export const getTeamReturnsFilterConfig = (): CardFilterConfig => {
  const allStatuses = Object.values(DataLogStatus);
  const pendingStatuses = allStatuses.filter(
    (status) =>
      status !== DataLogStatus.SUBMITTED &&
      status !== DataLogStatus.CANCELLED &&
      status !== DataLogStatus.EXCEPTIONS_MODIFIED &&
      status !== DataLogStatus.NOT_STARTED
  );

  return {
    filters: {
      status: {
        value: pendingStatuses,
        displayInUI: true,
      },
    },
    // Exclude NOT_STARTED as a safeguard
    excludeStatuses: [DataLogStatus.NOT_STARTED],
  };
};

/**
 * Get filter configuration for "Awaiting Review" card
 */
export const getAwaitingReviewFilterConfig = (): CardFilterConfig => {
  return {
    filters: {
      status: {
        value: [
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
        ],
        displayInUI: true,
      },
    },
  };
};

/**
 * Get filter configuration for "Submitted (month)" card
 * @param currentUser - Current user (optional)
 * @param viewType - View type: 'my' shows only current user's submissions, 'manager' shows all users' submissions
 */
export const getSubmittedThisMonthFilterConfig = (
  currentUser?: User,
  viewType: 'my' | 'manager' = 'my'
): CardFilterConfig | null => {
  // Construct userName to match the format used in renderAvatarOption: `${firstName} ${lastName}`.trim()
  const userName = currentUser
    ? (() => {
        const firstName = currentUser.firstName || '';
        const lastName = currentUser.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || currentUser.email || i18n.t('common.unknown');
      })()
    : null;

  const config: CardFilterConfig = {
    filters: {
      'dataLog.submissionDate': {
        value: DateOptions.THIS_MONTH,
        displayInUI: true,
      },
    },
  };

  // Add user filter only for 'my' view - managers should see all users' submissions
  if (viewType === 'my' && userName) {
    config.filters.user = {
      value: [userName],
      displayInUI: true,
    };
  }

  return config;
};

/**
 * Get filter configuration for "Returns by Due Date" card
 * @param dueDateType - Type of due date filter: 'overdue', 'dueToday', 'dueThisWeek', 'dueThisMonth', 'dueNextMonth', 'dueLater'
 * @param currentUser - Current user (optional)
 * @param viewType - View type: 'my' shows only current user's returns, 'manager' shows all users' returns
 */
export const getReturnsByDueDateFilterConfig = (
  dueDateType: string,
  currentUser?: User,
  viewType: 'my' | 'manager' = 'my'
): CardFilterConfig | null => {
  const userName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
      currentUser.email ||
      i18n.t('common.unknown')
    : null;

  const config: CardFilterConfig = {
    filters: {
      nextDueDate: {
        value: dueDateType,
        displayInUI: true,
      },
      // Status filter - open returns only (exclude CANCELLED, SUBMITTED, NOT_STARTED; include LOCKED)
      status: {
        value: [
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
        ],
        displayInUI: true,
      },
    },
    // Explicitly exclude these statuses as a safeguard
    excludeStatuses: [DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED, DataLogStatus.NOT_STARTED],
  };

  // Add user filter only for 'my' view - managers should see all users' returns for this due date
  // Explicitly check for 'manager' view and ensure user filter is NEVER added in that case
  if (viewType === 'manager') {
    // Do not add user filter - managers should see all users' returns
  } else if (viewType === 'my' && userName) {
    // Only add user filter for 'my' view
    config.filters.user = {
      value: [userName],
      displayInUI: true,
    };
  }

  return config;
};

/**
 * Get filter configuration for "Type of Returns" card
 * @param returnTypeName - Name of the return type to filter by
 * @param currentUser - Current user (optional)
 * @param viewType - View type: 'my' shows only current user's returns, 'manager' shows all users' returns
 */
export const getTypeOfReturnsFilterConfig = (
  returnTypeName: string,
  currentUser?: User,
  viewType: 'my' | 'manager' = 'my'
): CardFilterConfig | null => {
  const userName = currentUser
    ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
      currentUser.email ||
      i18n.t('common.unknown')
    : null;

  // Manager view: include NOT_STARTED; My view: exclude NOT_STARTED
  const statusValues =
    viewType === 'manager'
      ? [
          DataLogStatus.NOT_STARTED,
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
        ]
      : [
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
        ];

  const config: CardFilterConfig = {
    filters: {
      // Return name (return type name) - filter by return type name from entities (source of truth)
      // Do not add mappingName filter: item.mappingName can be "Country - Abbrev" and would exclude valid items
      returnName: {
        value: [returnTypeName],
        displayInUI: true,
      },
      // Status filter - open returns (+ NOT_STARTED for manager view only)
      status: {
        value: statusValues,
        displayInUI: true,
      },
    },
    // Explicitly exclude these statuses as a safeguard (manager: exclude NOT_STARTED from excludeStatuses)
    excludeStatuses:
      viewType === 'manager'
        ? [DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED]
        : [DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED, DataLogStatus.NOT_STARTED],
  };

  // Add user filter only for 'my' view - managers should see all users' returns for this type
  // Explicitly check for 'manager' view and ensure user filter is NEVER added in that case
  if (viewType === 'manager') {
    // Do not add user filter - managers should see all users' returns
  } else if (viewType === 'my' && userName) {
    // Only add user filter for 'my' view
    config.filters.user = {
      value: [userName],
      displayInUI: true,
    };
  }

  return config;
};

/**
 * Get filter configuration for "Your Role in Returns" card
 */
export const getYourRoleInReturnsFilterConfig = (
  role: 'preparer' | 'reviewer',
  currentUser?: User
): CardFilterConfig | null => {
  if (!currentUser) return null;

  const userName =
    `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
    currentUser.email ||
    i18n.t('common.unknown');

  return {
    filters: {
      // Use the specific role filter (preparer or reviewer)
      [role]: {
        value: [userName],
        displayInUI: true,
      },
      // Status filter - open returns only
      // Explicitly excludes: NOT_STARTED, SUBMITTED, CANCELLED
      // Includes only: LOCKED, SAVED, AWAITING_APPROVAL, INTERNAL_APPROVED, APPROVED, AWAITING_CLIENT_APPROVAL, CLIENT_APPROVED
      status: {
        value: [
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
        ],
        displayInUI: true,
      },
    },
    // Explicitly exclude NOT_STARTED, SUBMITTED, and CANCELLED as a safeguard
    // This ensures these statuses are never shown, even if they somehow get into the data
    excludeStatuses: [DataLogStatus.NOT_STARTED, DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED],
  };
};

/**
 * Get filter configuration for "Team Allocation" card - when clicking a preparer/reviewer name (manager view)
 * Shows returns where the selected person is the preparer or reviewer; excludes Submitted/Cancelled.
 */
export const getTeamAllocationPersonFilterConfig = (
  personName: string,
  role: 'preparer' | 'reviewer'
): CardFilterConfig => {
  return {
    filters: {
      [role]: {
        value: [personName],
        displayInUI: true,
      },
      status: {
        value: [
          DataLogStatus.NOT_STARTED,
          DataLogStatus.NOT_SAVED,
          DataLogStatus.LOCKED,
          DataLogStatus.SAVED,
          DataLogStatus.AWAITING_APPROVAL,
          DataLogStatus.INTERNAL_APPROVED,
          DataLogStatus.APPROVED,
          DataLogStatus.AWAITING_CLIENT_APPROVAL,
          DataLogStatus.CLIENT_APPROVED,
          DataLogStatus.EXCEPTIONS_MODIFIED,
        ],
        displayInUI: true,
      },
    },
    excludeStatuses: [DataLogStatus.SUBMITTED, DataLogStatus.CANCELLED],
  };
};

/**
 * Convert CardFilterConfig to URL search params for navigation
 */
export const cardFilterConfigToSearchParams = (config: CardFilterConfig): URLSearchParams => {
  const params = new URLSearchParams();
  
  // Store the entire config as JSON for easy parsing
  params.set('cardFilter', JSON.stringify(config));
  
  return params;
};

/**
 * Parse CardFilterConfig from URL search params
 */
export const parseCardFilterConfigFromSearchParams = (params: URLSearchParams): CardFilterConfig | null => {
  const cardFilterParam = params.get('cardFilter');
  if (!cardFilterParam) return null;
  
  try {
    return JSON.parse(cardFilterParam) as CardFilterConfig;
  } catch {
    return null;
  }
};
